import { ICommonAddItemToCartRequest, ICommonCart, ICommonRemoveItemFromCartRequest, IPrice } from '@cuspar/ywt.ch.model';
import { create } from 'zustand';
import { IAxiosResponse } from '../axios';
import { cartAPI } from './api/cart';
import { useAuthStore } from './authStore';

export type AddItemToCartResult = { success: true } | { success: false; reason: string };

type cartStore = {
  cart?: ICommonCart;
  prices: IPrice[];
  /**
   * With `suppressError` the failure is not toasted; the caller receives the
   * reason and decides how to show it (the batch importer shows it per row).
   */
  addItemToCart: (item: ICommonAddItemToCartRequest, suppressError?: boolean, timeoutMs?: number) => Promise<AddItemToCartResult>;
  removeItemFromCart: (cartId: string, request: ICommonRemoveItemFromCartRequest) => Promise<void>;
  getMyCart: (suppressError?: boolean) => Promise<void>;
  mergeLocalCart: (cartId: string) => Promise<void>;
  getCartById: (cartId: string) => Promise<void>;
  emptyCart: () => void;
  convertToOrder: () => Promise<string | undefined>;
};

const toResult = (response: IAxiosResponse<ICommonCart>): AddItemToCartResult => (response.success ? { success: true } : { success: false, reason: response.reason });

export const useCartStore = create<cartStore>((set, get) => ({
  cart: undefined,
  prices: [],
  addItemToCart: async (item: ICommonAddItemToCartRequest, suppressError: boolean = false, timeoutMs?: number) => {
    // get the cart from the current state
    const cart = get().cart;
    // if the cart is not defined, create a new cart
    if (!cart) {
      // get user id from the store
      const response = await cartAPI.createCartWithItem(item, suppressError, timeoutMs);
      if (response.success) {
        set({ cart: response.data });
        // save the cart Id to the local storage if the user is not authenticated
        if (!useAuthStore.getState().user) localStorage.setItem('cartId', response.data.id);
      }
      return toResult(response);
    }
    // add the item to the cart
    const response = await cartAPI.addItemToCart(cart.id, item, suppressError, timeoutMs);
    if (response.success) set({ cart: response.data });
    return toResult(response);
  },
  removeItemFromCart: async (cartId: string, request: ICommonRemoveItemFromCartRequest) => {
    const response = await cartAPI.removeItemFromCart(cartId, request);
    if (response.success) set({ cart: response.data });
  },
  getMyCart: async (suppressError?: boolean) => {
    const response = await cartAPI.getMyCart(suppressError);
    if (response.success) set({ cart: response.data });
  },
  getCartById: async (cartId: string) => {
    const response = await cartAPI.getCartById(cartId);
    if (response.success) set({ cart: response.data });
  },
  mergeLocalCart: async (cartId: string) => {
    const response = await cartAPI.mergeLocalCart(cartId);
    if (response.success) {
      set({ cart: response.data });
      // Forget the anonymous cart id only after it was actually merged — a
      // transient failure must not silently lose the customer's cart.
      localStorage.removeItem('cartId');
    }
  },
  emptyCart: () => set({ cart: undefined }),
  convertToOrder: async () => {
    const response = await cartAPI.convertToOrder();
    return response.success ? response.data : undefined;
  },
}));
